import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  useReactFlow,
  useNodesInitialized,
  useUpdateNodeInternals,
  addEdge,
  type Node,
  type Edge,
  type NodeTypes,
  type OnConnect,
  type OnNodeDrag,
  type OnNodesChange,
  type OnEdgesChange,
  type IsValidConnection,
  MarkerType,
  applyNodeChanges,
  applyEdgeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTheme } from 'next-themes';
import type { FlowNode, FlowEdge as FlowEdgeType, FlowRunNode } from '@reflex/flows';
import { latestByNodeId } from '@reflex/flows';
import { FlowAgentNode } from './FlowAgentNode';
import { FlowAgentTaskNode } from './FlowAgentTaskNode';
import { FlowApprovalNode } from './FlowApprovalNode';
import { FlowRouterNode } from './FlowRouterNode';
import { FlowLLMNode } from './FlowLLMNode';
import { FlowActionNode } from './FlowActionNode';
import { FlowTriggerNode } from './FlowTriggerNode';
import { FlowFilterNode } from './FlowFilterNode';
import {
  layoutGraph,
  rankSepForEdgeLabels,
  orderDecisionOptionsByTarget,
  type LayoutDirection,
} from '../../shared/flow-graph-layout';
import { FlowLayoutDirectionProvider } from './flow-layout-direction-context';

const nodeTypes: NodeTypes = {
  agent: FlowAgentNode,
  agent_task: FlowAgentTaskNode,
  approval: FlowApprovalNode,
  router: FlowRouterNode,
  llm: FlowLLMNode,
  action: FlowActionNode,
  trigger: FlowTriggerNode,
  filter: FlowFilterNode,
};

// Stable empty fallbacks so the defensive `??` defaults inside FlowGraphInner
// don't allocate a fresh array on every render and trigger downstream effect
// re-runs.
const EMPTY_NODES: FlowNode[] = [];
const EMPTY_EDGES: FlowEdgeType[] = [];

interface FlowGraphProps {
  nodes: FlowNode[];
  edges: FlowEdgeType[];
  runNodes?: FlowRunNode[];
  /**
   * Raw `triggerData` payload from the active run, when rendering a run-
   * specific graph. Forwarded to trigger nodes so plugin-contributed
   * trigger renderers can show payload-aware content (e.g. Sentry issue
   * title/project) inside the graph node.
   */
  triggerData?: Record<string, unknown> | null;
  /**
   * Id of the trigger node the run was anchored at (`run.triggerNodeId`).
   * `triggerData` is attached only to this node when set; legacy runs
   * without it attach the payload to every trigger node.
   */
  triggerNodeId?: string | null;
  className?: string;
  interactive?: boolean;
  /**
   * When true, the graph is laid out automatically (left-to-right by
   * default) using the saved positions only as a starting hint. Layout is
   * applied after node dimensions have been measured and is purely
   * display-time — `onNodesChange` is never called as a result of layout.
   */
  autoLayout?: boolean;
  /**
   * Direction passed to the layered layout algorithm and node handle
   * orientation. Defaults to 'LR' (target left, source right).
   */
  layoutDirection?: LayoutDirection;
  /**
   * If set, the camera centers on this node at a legible zoom instead of
   * fitting the entire graph. Use this to follow the currently running node
   * during a live run.
   */
  focalNodeId?: string | null;
  onNodesChange?: (nodes: FlowNode[]) => void;
  onEdgesChange?: (edges: FlowEdgeType[]) => void;
  onNodeSelect?: (node: FlowNode | null) => void;
  onEdgeSelect?: (edge: FlowEdgeType | null) => void;
  onDropNode?: (type: string, position: { x: number; y: number }) => void;
  /** When set to null externally, clears React Flow's internal selection. */
  selectedNodeId?: string | null;
}

const MIN_LEGIBLE_ZOOM = 0.6;
const FOCAL_ZOOM = 1;

let edgeIdCounter = 0;

function buildReactFlowNode(
  node: FlowNode,
  latestRunNodes?: Map<string, FlowRunNode>,
  triggerData?: Record<string, unknown> | null,
  triggerNodeId?: string | null,
): Node {
  const status = latestRunNodes?.get(node.id)?.status ?? 'pending';
  // Attach the run payload to the fired trigger node (or every trigger
  // node for legacy runs without `triggerNodeId`) so plugin renderers can
  // show payload-aware content.
  const isPayloadTarget =
    node.type === 'trigger' && triggerData != null && (!triggerNodeId || node.id === triggerNodeId);
  return {
    id: node.id,
    type: node.type,
    position: node.position,
    data: {
      label: node.name,
      config: node.config,
      status,
      nodeType: node.type,
      ...(isPayloadTarget ? { triggerData } : {}),
    },
  };
}

function getEdgeColor(condition?: string): string {
  if (!condition) return 'var(--status-neutral)';
  if (condition === 'approved') return 'var(--status-success)';
  if (condition === 'rejected') return 'var(--status-error)';
  return 'var(--status-accent-violet)';
}

function toReactFlowEdges(
  flowEdges: FlowEdgeType[],
  flowNodes: FlowNode[],
  runNodes?: FlowRunNode[],
): Edge[] {
  const nodeTypeById = new Map(flowNodes.map((n) => [n.id, n.type]));
  return flowEdges.map((edge) => {
    const color = getEdgeColor(edge.condition);
    const label = edge.label ?? edge.condition;
    const sourceIsApproval = nodeTypeById.get(edge.source) === 'approval';
    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: sourceIsApproval && edge.condition ? edge.condition : undefined,
      label,
      type: 'smoothstep',
      animated: runNodes
        ? runNodes.some((rn) => rn.nodeId === edge.source && rn.status === 'running')
        : false,
      style: {
        stroke: color,
        strokeWidth: edge.condition ? 1.5 : 2,
        strokeDasharray: edge.condition ? '6 4' : undefined,
      },
      labelStyle: label ? { fill: 'var(--foreground)', fontSize: 11, fontWeight: 500 } : undefined,
      labelBgStyle: label ? { fill: color, fillOpacity: 0.85 } : undefined,
      labelBgPadding: label ? ([6, 4] as [number, number]) : undefined,
      labelBgBorderRadius: 4,
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
    };
  });
}

/**
 * React Flow v12 in controlled mode needs onNodesChange to persist internal
 * state like measured dimensions. This component keeps an internal mirror of
 * the React Flow arrays, merging prop changes without losing RF internals.
 *
 * Parent callbacks are never called from inside state updaters to avoid the
 * "Cannot update component X while rendering component Y" React error.
 * A suppressSync ref prevents the prop→internal merge effect from firing
 * when the prop change originated from our own forwarding callback.
 */
function FlowGraphInner({
  nodes: flowNodesProp,
  edges: flowEdgesProp,
  runNodes,
  triggerData,
  triggerNodeId,
  className,
  interactive = false,
  autoLayout = false,
  layoutDirection = 'LR',
  focalNodeId,
  onNodesChange: onNodesChangeProp,
  onEdgesChange: onEdgesChangeProp,
  onNodeSelect,
  onEdgeSelect,
  onDropNode,
  selectedNodeId,
}: FlowGraphProps) {
  const { screenToFlowPosition, fitView, setCenter, getNode } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const nodesInitialized = useNodesInitialized();
  const { resolvedTheme } = useTheme();
  const colorMode = resolvedTheme === 'dark' ? 'dark' : 'light';

  // Tolerate malformed parent props (a stale or partially-loaded flow can
  // arrive here with `nodes`/`edges` undefined despite the type). Without
  // this guard the initial `useState` initializer crashes in `mountStateImpl`
  // and surfaces as an unhandled error that wedges other concurrent stories.
  const flowNodes = flowNodesProp ?? EMPTY_NODES;
  const flowEdges = flowEdgesProp ?? EMPTY_EDGES;

  // Latest run-node per flow node by the numeric `iteration` column — the
  // node list carries no ordering guarantee, so a positional pick could show
  // a stale iteration. Built once per render; buildReactFlowNode reads it
  // per node.
  const latestRunNodes = useMemo(
    () => (runNodes ? latestByNodeId(runNodes) : undefined),
    [runNodes],
  );

  const [internalNodes, setInternalNodes] = useState<Node[]>(() =>
    flowNodes.map((n) => buildReactFlowNode(n, latestRunNodes, triggerData, triggerNodeId)),
  );
  const [internalEdges, setInternalEdges] = useState<Edge[]>(() =>
    toReactFlowEdges(flowEdges, flowNodes, runNodes),
  );

  const flowNodesRef = useRef(flowNodes);
  const flowEdgesRef = useRef(flowEdges);
  const onNodeSelectRef = useRef(onNodeSelect);
  const onEdgeSelectRef = useRef(onEdgeSelect);
  const suppressNodeSyncRef = useRef(false);
  const suppressEdgeSyncRef = useRef(false);

  onNodeSelectRef.current = onNodeSelect;
  onEdgeSelectRef.current = onEdgeSelect;

  // Re-measure handle positions when layout direction changes.
  useEffect(() => {
    for (const node of flowNodes) {
      updateNodeInternals(node.id);
    }
  }, [layoutDirection, flowNodes, updateNodeInternals]);

  // When the parent clears selection (e.g. × button), clear React
  // Flow's internal selected state so clicking the same node again
  // re-fires onSelectionChange.
  useEffect(() => {
    if (selectedNodeId === null) {
      setInternalNodes((prev) =>
        prev.some((n) => n.selected)
          ? prev.map((n) => (n.selected ? { ...n, selected: false } : n))
          : prev,
      );
      setInternalEdges((prev) =>
        prev.some((e) => e.selected)
          ? prev.map((e) => (e.selected ? { ...e, selected: false } : e))
          : prev,
      );
    }
  }, [selectedNodeId]);

  // Merge prop changes into internal nodes, preserving RF internals (measured, width, height).
  // Skipped when the prop change originated from our own forwarding callback.
  useEffect(() => {
    flowNodesRef.current = flowNodes;
    if (suppressNodeSyncRef.current) {
      suppressNodeSyncRef.current = false;
      return;
    }
    setInternalNodes((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));
      const nodeIds = new Set(flowNodes.map((n) => n.id));
      const prevIds = new Set(prev.map((n) => n.id));
      const sameSet = nodeIds.size === prevIds.size && [...nodeIds].every((id) => prevIds.has(id));

      if (!sameSet) {
        return flowNodes.map((n) =>
          buildReactFlowNode(n, latestRunNodes, triggerData, triggerNodeId),
        );
      }
      return flowNodes.map((n) => {
        const existing = prevById.get(n.id);
        const fresh = buildReactFlowNode(n, latestRunNodes, triggerData, triggerNodeId);
        if (!existing) return fresh;
        return { ...existing, ...fresh };
      });
    });
  }, [flowNodes, latestRunNodes, triggerData, triggerNodeId]);

  useEffect(() => {
    flowEdgesRef.current = flowEdges;
    if (suppressEdgeSyncRef.current) {
      suppressEdgeSyncRef.current = false;
      return;
    }
    setInternalEdges(toReactFlowEdges(flowEdges, flowNodes, runNodes));
  }, [flowEdges, runNodes, flowNodes]);

  // Auto-layout: re-rank nodes left-to-right (or per `layoutDirection`) once
  // dimensions have been measured. This is display-only — saved positions are
  // never overwritten because we patch internalNodes directly and never invoke
  // `onNodesChangeProp`.
  //
  // `layoutAppliedKey` is *state* (not a ref) so that the camera effect's
  // dependency array sees the change and refires only AFTER React commits the
  // post-layout positions. A ref would change synchronously inside the layout
  // effect, causing the camera to fire in the same commit using the still-
  // stale pre-layout positions in React Flow's store — that's the "blank
  // canvas until you press fit" bug.
  const [layoutAppliedKey, setLayoutAppliedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!autoLayout || !nodesInitialized) return;
    const nodeKey = internalNodes
      .map((n) => n.id)
      .sort()
      .join(',');
    const edgeKey = internalEdges
      .map((e) => e.id)
      .sort()
      .join(',');
    const key = `${layoutDirection}|${nodeKey}|${edgeKey}`;
    if (key === layoutAppliedKey) return;
    if (internalNodes.length === 0) return;

    const sizes = new Map<string, { width: number; height: number }>();
    for (const n of internalNodes) {
      const measured = n.measured;
      const w = measured?.width ?? n.width;
      const h = measured?.height ?? n.height;
      if (w && h && w > 0 && h > 0) {
        sizes.set(n.id, { width: w, height: h });
      }
    }

    const positions = layoutGraph({
      nodes: internalNodes.map((n) => ({ id: n.id, type: n.type })),
      edges: internalEdges.map((e) => ({ source: e.source, target: e.target })),
      direction: layoutDirection,
      nodeSizes: sizes,
      // Edge labels render centered in the rank gap; widen it so the longest
      // label isn't buried under the neighboring nodes.
      rankSep: rankSepForEdgeLabels(
        internalEdges.map((e) => (typeof e.label === 'string' ? e.label : undefined)),
      ),
    });

    setInternalNodes((prev) =>
      prev.map((n) => {
        const pos = positions.get(n.id);
        return pos ? { ...n, position: pos } : n;
      }),
    );
    setLayoutAppliedKey(key);
  }, [
    autoLayout,
    nodesInitialized,
    internalNodes,
    internalEdges,
    layoutDirection,
    layoutAppliedKey,
  ]);

  // Reset layout when autoLayout is toggled off so we recompute on re-enable.
  useEffect(() => {
    if (!autoLayout) setLayoutAppliedKey(null);
  }, [autoLayout]);

  // Keep each approval node's per-option source handles stacked in the same
  // vertical order as the targets they connect to; otherwise conditional
  // edges cross each other right at the source. Display-time only — the
  // reordered options live on the internal React Flow node data and are
  // never written back to the flow definition. Converges in one pass: the
  // second run finds the options already ordered and returns `prev`
  // unchanged, so the effect doesn't loop.
  useEffect(() => {
    setInternalNodes((prev) => {
      const centerById = new Map(
        prev.map((n) => {
          const halfW = (n.measured?.width ?? n.width ?? 0) / 2;
          const halfH = (n.measured?.height ?? n.height ?? 0) / 2;
          return [
            n.id,
            layoutDirection === 'TB' ? n.position.x + halfW : n.position.y + halfH,
          ] as const;
        }),
      );
      let changed = false;
      const next = prev.map((n) => {
        if (n.type !== 'approval') return n;
        const data = n.data as {
          config?: { decisionOptions?: { value: string; label: string }[] };
        };
        const options = data.config?.decisionOptions;
        if (!options || options.length < 2) return n;
        const sorted = orderDecisionOptionsByTarget(options, n.id, internalEdges, centerById);
        if (sorted.every((option, i) => option.value === options[i].value)) return n;
        changed = true;
        return {
          ...n,
          data: { ...n.data, config: { ...data.config, decisionOptions: sorted } },
        };
      });
      return changed ? next : prev;
    });
  }, [internalNodes, internalEdges, layoutDirection]);

  // Camera: focus on a focal node when one is provided, otherwise fit the
  // whole graph but never zoom out below the legibility floor — extra nodes
  // are allowed to flow off-canvas.
  //
  // The cameraKey includes `layoutAppliedKey` so a freshly-applied layout
  // forces the camera to re-frame against the new positions. Without it,
  // the camera would only fire on focal-id or node-id-set changes and miss
  // the layout transition.
  const lastCameraKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!nodesInitialized) return;
    if (internalNodes.length === 0) return;
    // Wait until auto-layout has produced positions before we frame the view.
    if (autoLayout && layoutAppliedKey === null) return;

    const idsKey = internalNodes
      .map((n) => n.id)
      .sort()
      .join(',');
    const cameraKey = `${focalNodeId ?? ''}|${layoutAppliedKey ?? ''}|${idsKey}`;
    if (cameraKey === lastCameraKeyRef.current) return;

    if (focalNodeId) {
      const target = getNode(focalNodeId) ?? internalNodes.find((n) => n.id === focalNodeId);
      if (target) {
        const measured = target.measured;
        const width = measured?.width ?? target.width ?? 220;
        const height = measured?.height ?? target.height ?? 80;
        const cx = target.position.x + width / 2;
        const cy = target.position.y + height / 2;
        lastCameraKeyRef.current = cameraKey;
        void setCenter(cx, cy, { zoom: FOCAL_ZOOM, duration: 400 });
        return;
      }
    }

    lastCameraKeyRef.current = cameraKey;
    void fitView({ padding: 0.2, minZoom: MIN_LEGIBLE_ZOOM, duration: 400 });
  }, [
    nodesInitialized,
    autoLayout,
    focalNodeId,
    internalNodes,
    layoutAppliedKey,
    setCenter,
    fitView,
    getNode,
  ]);

  const prevNodeIdsRef = useRef(flowNodes.map((n) => n.id).join(','));
  useEffect(() => {
    const ids = flowNodes.map((n) => n.id).join(',');
    if (ids !== prevNodeIdsRef.current && ids.length > 0) {
      prevNodeIdsRef.current = ids;
      lastCameraKeyRef.current = null;
      // Force a re-layout when the underlying flow node set changes; the
      // layout effect's own key check handles same-set-different-positions.
      if (autoLayout) setLayoutAppliedKey(null);
    }
  }, [flowNodes, autoLayout]);

  const handleNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setInternalNodes((prev) => applyNodeChanges(changes, prev));

      // Surface node selection to the parent so the properties panel
      // can swap in. React Flow v12 reports single-click selection as
      // `{ type: 'select' }` changes here, NOT via `onSelectionChange`.
      const selectChanges = changes.filter(
        (c): c is Extract<typeof c, { type: 'select' }> => c.type === 'select',
      );
      if (selectChanges.length > 0) {
        const selected = selectChanges.find((c) => c.selected);
        if (selected) {
          const flowNode = flowNodesRef.current.find((n) => n.id === selected.id) ?? null;
          onNodeSelectRef.current?.(flowNode);
          onEdgeSelectRef.current?.(null);
        } else {
          // Pane click / Esc: everything is deselected.
          onNodeSelectRef.current?.(null);
        }
      }

      // Forward position changes to parent on drag end only (dragging === false).
      // Computed from the changes array + flowNodesRef — never inside a state updater.
      if (onNodesChangeProp) {
        const posChanges = changes.filter(
          (c): c is Extract<typeof c, { type: 'position' }> =>
            c.type === 'position' && c.dragging === false && c.position != null,
        );
        if (posChanges.length > 0) {
          const updated = [...flowNodesRef.current];
          for (const ch of posChanges) {
            const idx = updated.findIndex((n) => n.id === ch.id);
            if (idx !== -1 && ch.position) {
              updated[idx] = { ...updated[idx], position: ch.position };
            }
          }
          suppressNodeSyncRef.current = true;
          onNodesChangeProp(updated);
        }

        const removeIds = changes
          .filter((c): c is Extract<typeof c, { type: 'remove' }> => c.type === 'remove')
          .map((c) => c.id);
        if (removeIds.length > 0) {
          suppressNodeSyncRef.current = true;
          onNodesChangeProp(flowNodesRef.current.filter((n) => !removeIds.includes(n.id)));
        }
      }
    },
    [onNodesChangeProp],
  );

  const handleEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      setInternalEdges((prev) => applyEdgeChanges(changes, prev));

      // Surface edge selection the same way as node selection above.
      const selectChanges = changes.filter(
        (c): c is Extract<typeof c, { type: 'select' }> => c.type === 'select',
      );
      if (selectChanges.length > 0) {
        const selected = selectChanges.find((c) => c.selected);
        if (selected) {
          const flowEdge = flowEdgesRef.current.find((e) => e.id === selected.id) ?? null;
          onEdgeSelectRef.current?.(flowEdge);
          onNodeSelectRef.current?.(null);
        } else {
          onEdgeSelectRef.current?.(null);
        }
      }

      if (onEdgesChangeProp) {
        const removeIds = changes
          .filter((c): c is Extract<typeof c, { type: 'remove' }> => c.type === 'remove')
          .map((c) => c.id);
        if (removeIds.length > 0) {
          suppressEdgeSyncRef.current = true;
          onEdgesChangeProp(flowEdgesRef.current.filter((e) => !removeIds.includes(e.id)));
        }
      }
    },
    [onEdgesChangeProp],
  );

  const handleConnect: OnConnect = useCallback(
    (params) => {
      if (!onEdgesChangeProp) return;
      edgeIdCounter++;
      const newId = `e-${params.source}-${params.target}-${edgeIdCounter}`;
      const newEdge: FlowEdgeType = {
        id: newId,
        source: params.source!,
        target: params.target!,
      };

      setInternalEdges((prev) =>
        addEdge({ ...params, id: newId, markerEnd: { type: MarkerType.ArrowClosed } }, prev),
      );

      suppressEdgeSyncRef.current = true;
      onEdgesChangeProp([...flowEdgesRef.current, newEdge]);
    },
    [onEdgesChangeProp],
  );

  // Trigger nodes are sources: they render no target handle, and this guard
  // also blocks programmatic/edge-drop attempts to land an edge on one.
  const isValidConnection: IsValidConnection = useCallback((connection) => {
    if (!connection.target) return true;
    const target = flowNodesRef.current.find((n) => n.id === connection.target);
    return target?.type !== 'trigger';
  }, []);

  const handleSelectionChange = useCallback(
    ({ nodes: selNodes, edges: selEdges }: { nodes: Node[]; edges: Edge[] }) => {
      // Promotes positive selections only — used for box-select, where multiple
      // items enter the selection set at once. Selection clearing is owned by
      // handleNodesChange / handleEdgesChange, which fire deterministic select-
      // false changes for every deselection path (clicking another node, pane
      // click, Esc, ×). Clearing here would race with those handlers and could
      // wipe a freshly-set selection.
      if (selNodes.length > 0) {
        const flowNode = flowNodesRef.current.find((n) => n.id === selNodes[0].id) ?? null;
        onNodeSelectRef.current?.(flowNode);
      } else if (selEdges.length > 0) {
        const flowEdge = flowEdgesRef.current.find((e) => e.id === selEdges[0].id) ?? null;
        onEdgeSelectRef.current?.(flowEdge);
      }
    },
    [],
  );

  const handleNodeDragStart: OnNodeDrag = useCallback((_event, node) => {
    const flowNode = flowNodesRef.current.find((n) => n.id === node.id) ?? null;
    onNodeSelectRef.current?.(flowNode);
    onEdgeSelectRef.current?.(null);
  }, []);

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      const nodeType = event.dataTransfer.getData('application/reactflow-nodetype');
      if (!nodeType || !onDropNode) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      onDropNode(nodeType, position);
    },
    [onDropNode, screenToFlowPosition],
  );

  return (
    <div
      className={className ?? 'h-full w-full'}
      onDragOver={interactive ? handleDragOver : undefined}
      onDrop={interactive ? handleDrop : undefined}
    >
      <ReactFlow
        nodes={internalNodes}
        edges={internalEdges}
        nodeTypes={nodeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={interactive ? handleConnect : undefined}
        isValidConnection={isValidConnection}
        onSelectionChange={onNodeSelect || onEdgeSelect ? handleSelectionChange : undefined}
        onNodeDragStart={interactive && onNodeSelect ? handleNodeDragStart : undefined}
        nodesDraggable={interactive && !autoLayout}
        nodesConnectable={interactive && !autoLayout}
        elementsSelectable={interactive || !!onNodeSelect || !!onEdgeSelect}
        fitView
        minZoom={MIN_LEGIBLE_ZOOM}
        maxZoom={2}
        deleteKeyCode={interactive ? ['Backspace', 'Delete'] : []}
        proOptions={{ hideAttribution: true }}
        colorMode={colorMode}
      >
        <Background gap={16} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

export function FlowGraph(props: FlowGraphProps) {
  const layoutDirection = props.layoutDirection ?? 'LR';
  return (
    <FlowLayoutDirectionProvider direction={layoutDirection}>
      <ReactFlowProvider>
        <FlowGraphInner {...props} layoutDirection={layoutDirection} />
      </ReactFlowProvider>
    </FlowLayoutDirectionProvider>
  );
}
